import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { formatPhoneDisplay } from '@/util/phoneFormatter'
import { calculateSegments } from '@/util/smsSegments'
import PhoneNumberInput from '@/components/PhoneNumberInput.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SMSConversationDrawer',
  props: {
  show: { type: Boolean, default: false },
  conversationId: { type: Number, default: null },
  phone: { type: String, default: null },
  newMessage: { type: Boolean, default: false }
},
  emits: ["close", "sent"],
  setup(__props, { emit }: { emit: ({ (e: 'close'): void; (e: 'sent', message: any): void }), expose: any, slots: any, attrs: any }) {

const props = __props

// The chat window for one customer's texts: the conversation's messages as bubbles,
// oldest first, and a composer that sends a staff reply through sms-conversation
// (which hands off to sms-send-raw, so consent and gating are enforced there).
// Opened from a log row with the row's conversation id, or with a phone number when
// no conversation exists yet. Opening marks the thread read; while open it refreshes
// every 15 seconds so delivery chips and new replies arrive without a reload.



const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/sms-conversations`

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued', sending: 'Sending', sent: 'Sent', delivered: 'Delivered',
  undelivered: 'Undelivered', failed: 'Failed', received: 'Received'
}
const STATUS_COLORS: Record<string, string> = {
  delivered: '#00f2c3', sent: '#1d8cf8', sending: '#1d8cf8', queued: '#1d8cf8',
  undelivered: '#fd5d93', failed: '#fd5d93', received: '#e14eca'
}

const loading = ref(false)
const sending = ref(false)
const conversation = ref<any>(null)
const phoneE164 = ref<string>('')
const messages = ref<any[]>([])
const draft = ref('')
const thread = ref<HTMLElement | null>(null)
// New message mode: the operator types a number, then the thread for it loads
const newPhone = ref('')
const started = ref(false)
const activePhone = ref<string | null>(null)
let refreshTimer: ReturnType<typeof setInterval> | null = null

const title = computed(() => 'Text messages')
const headerTitle = computed(() => (conversation.value && conversation.value.customer_name) ? 'Customer' : 'Phone number')
const phoneDisplay = computed(() => formatPhoneDisplay(phoneE164.value || activePhone.value || props.phone || ''))
const segmentText = computed(() => {
  const info = calculateSegments(draft.value)
  if (!draft.value) return 'One segment is one SMS credit'
  return `${info.length} characters, ${info.segments} segment${info.segments === 1 ? '' : 's'} (${info.encoding})`
})

function statusLabel(status: string) { return STATUS_LABELS[status] || status }
function statusColor(status: string) { return STATUS_COLORS[status] || '#7d7d7d' }
function senderLabel(m: any) {
  if (m.sender_first_name || m.sender_last_name) return [m.sender_first_name, m.sender_last_name].filter(Boolean).join(' ')
  if (m.automation_name) return m.automation_name
  if (m.template_name) return m.template_name
  if (m.sms_type === 'test') return 'Test message'
  return 'Automation'
}
function formatTime(value: string) {
  try { return formatGroupTime(value) } catch (e) { return '' }
}
function dayOf(value: string) {
  try { return formatGroupDate(value) } catch (e) { return String(value).slice(0, 10) }
}
// A day label above the first message of each day
function dayLabel(m: any, i: number) {
  const day = dayOf(m.timestamp)
  if (i === 0) return day
  return day === dayOf(messages.value[i - 1].timestamp) ? '' : day
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function queryString() {
  const params = new URLSearchParams()
  if (props.conversationId) params.append('conversation_id', String(props.conversationId))
  else if (activePhone.value || props.phone) params.append('phone', activePhone.value || props.phone || '')
  return params.toString()
}

function startThread() {
  if (!newPhone.value) return
  activePhone.value = newPhone.value
  started.value = true
  load()
}

async function scrollToEnd() {
  await nextTick()
  const el = thread.value
  if (el && el.lastElementChild) (el.lastElementChild as HTMLElement).scrollIntoView({ block: 'end' })
}

async function load(showLoader = true) {
  if (!props.conversationId && !props.phone && !activePhone.value) return
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}?${queryString()}`, config)
    conversation.value = response.data.conversation || null
    phoneE164.value = response.data.phone_e164 || ''
    messages.value = response.data.messages || []
    if (showLoader) await scrollToEnd()
  } catch (err: any) {
    if (showLoader) {
      proxy.$notify({ type: 'danger', message: 'Could not load this conversation. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
    }
  } finally {
    loading.value = false
  }
}

async function send() {
  const text = draft.value.trim()
  if (!text || sending.value) return
  sending.value = true
  try {
    const config = await authConfig()
    const payload: any = { body: text }
    if (conversation.value && conversation.value.id) payload.conversation_id = conversation.value.id
    else payload.phone = phoneE164.value || activePhone.value || props.phone
    const response = await axios.post(baseUrl, payload, config)
    if (response.data.message) messages.value.push(response.data.message)
    draft.value = ''
    emit('sent', response.data.message)
    await scrollToEnd()
    // The thread row now exists even if this was the first message to the number
    if (!conversation.value && response.data.conversation_id) await load(false)
  } catch (err: any) {
    const raw = err?.response?.data
    let parsed: any = raw
    if (typeof raw === 'string') {
      try { parsed = JSON.parse(raw) } catch (e) { parsed = { error: raw } }
    }
    proxy.$notify({ type: 'danger', message: parsed?.error || 'The message could not be sent. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    sending.value = false
  }
}

function onClose() {
  emit('close')
}
function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

function stopRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    draft.value = ''
    conversation.value = null
    messages.value = []
    newPhone.value = ''
    started.value = false
    activePhone.value = null
    if (!props.newMessage) load()
    stopRefresh()
    refreshTimer = setInterval(() => load(false), 15000)
  } else {
    document.removeEventListener('keydown', onEscKey)
    stopRefresh()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
  stopRefresh()
})

return { __sfc: true,props, emit, proxy, companyId, companyGroupId, baseUrl, STATUS_LABELS, STATUS_COLORS, loading, sending, conversation, phoneE164, messages, draft, thread, newPhone, started, activePhone, refreshTimer, title, headerTitle, phoneDisplay, segmentText, statusLabel, statusColor, senderLabel, formatTime, dayOf, dayLabel, authConfig, queryString, startThread, scrollToEnd, load, send, onClose, onEscKey, stopRefresh, tagChipStyle, PhoneNumberInput }
}

})